import { isDefined, isNonEmpty } from '@sgme/fp';
import { SPIntl } from 'contexts/intl/intlContext';
import { IntlKey } from 'locales';
import { DEFAULT_ERROR_SEVERITY, FormError, TranslatedValidationError } from 'models/formError';

function translateValidationErrorValues(
  values: Record<string, unknown> | undefined,
  { formatMessage, formatDisplayedDate }: SPIntl,
) {
  if (isDefined(values)) {
    return Object.keys(values).reduce<Record<string, unknown>>((result, key) => {
      const value = values[key];
      if (typeof value === 'string') {
        if (key.includes('date')) {
          result[key] = formatDisplayedDate(value);
        } else if (isNonEmpty(value)) {
          result[key] = formatMessage({ id: value as IntlKey, defaultMessage: value });
        } else {
          result[key] = value;
        }
      } else {
        result[key] = value;
      }
      return result;
    }, {});
  }
  return {};
}

export function translateValidationError<Form, AllKeys extends string, ErrorPrefix extends string>(
  error: FormError<Form, AllKeys, ErrorPrefix>,
  intl: SPIntl,
  errorPrefix: ErrorPrefix,
): TranslatedValidationError | undefined {
  const translationKey = intl.idExists(error.message) ? error.message : error.translationKey;
  const message =
    translationKey === undefined
      ? error.message
      : intl.formatMessage(
          { id: (errorPrefix + translationKey) as IntlKey, defaultMessage: translationKey },
          {
            strong: (...chunks) => `<strong>${chunks}</strong>`,
            ...translateValidationErrorValues(error.values, intl),
          },
        );
  return message !== undefined
    ? {
        severity: error.severity ?? DEFAULT_ERROR_SEVERITY,
        isDisplayedInSummary: error.isDisplayedInSummary ?? error.isDisplayedInResume,
        isDisplayedInUnderlyings: error.isDisplayedInUnderlyings,
        message,
      }
    : undefined;
}
